// Volume detail (ui-spec §6.11): the detail template over the live Volume, one route per tab. The one labelled action
// is Retry import on a Volume whose import failed; Clear and Delete sit in `⋯` and confirm first.
import { linkTo, useKObject } from "@nodus/api";
import { Button, cliCommand, usePrefs } from "@nodus/ui";
import { useNavigate } from "@tanstack/react-router";
import { DetailPage, DetailSection } from "../../app/templates";
import { sizeLabel, importFix, type Scope, type Volume } from "./model";
import { canRetryImport, useVolumeActions } from "./volume-actions";
import { CostTab, OverviewTab, RevisionsTab } from "./volume-tabs";

export const VOLUME_TABS = ["overview", "revisions", "cost"] as const;
export type VolumeTab = (typeof VOLUME_TABS)[number];

export function VolumeDetail({ scope, name, tab }: { scope: Scope; name: string; tab: VolumeTab }) {
  const prefs = usePrefs();
  const navigate = useNavigate();
  const q = useKObject<Volume>("Volume", name, scope);
  const v = q.object;
  const actions = useVolumeActions(scope, { onDeleted: () => void navigate({ href: linkTo({ kind: "Volume" }, scope) }) });
  const cmd = (args: string[]) => cliCommand(args, scope, { omitScope: prefs.omitScope });
  const ls = cmd(["volume", "ls", name]);
  const describe = cmd(["describe", `volume/${name}`]);
  const retry = v && canRetryImport(v) ? <Button onClick={() => void actions.retryImport(v)}>Retry import</Button> : null;

  return (
    <DetailPage
      kind="Volume"
      name={name}
      scope={scope}
      tab={tab}
      crumbs={[{ label: "Volumes", href: linkTo({ kind: "Volume" }, scope) }]}
      loading={q.isLoading}
      error={q.error}
      notFound={q.notFound}
      {...(v ? { object: v } : {})}
      editable
      commands={[
        { label: "Describe", cli: describe },
        { label: "Put files", cli: cmd(["volume", "put", name, "./dataset", "/train"]) },
        { label: "List files", cli: ls },
        { label: "Download a file", cli: cmd(["volume", "get", name, "/train/part-0.parquet", "./"]) },
        { label: "Clear", cli: cmd(["volume", "clear", name]) },
        { label: "Delete", cli: cmd(["delete", `volume/${name}`]) },
      ]}
      header={{
        cli: ls,
        status: { kind: "Volume", phase: v?.status?.phase, reason: v?.status?.reason, message: v?.status?.message },
        meta: [v?.spec?.accessMode ?? null, v ? `${sizeLabel(v.spec?.size)} limit` : null, v?.status?.revision ? `Revision #${v.status.revision}` : null].filter(Boolean),
        primaryAction: retry,
        ...(v?.status?.phase === "Failed"
          ? { failure: { reason: v.status.reason, message: v.status.message, ...(canRetryImport(v) ? { fix: importFix(v) } : {}), describeCommand: describe } }
          : {}),
        actions: v ? actions.menu(v) : [],
      }}
    >
      {v ? (
        <>
          {tab === "overview" ? (
            <>
              <OverviewTab volume={v} scope={scope} />
              <DetailSection id="cost" title="Cost">
                <CostTab volume={v} scope={scope} />
              </DetailSection>
            </>
          ) : null}
          {tab === "revisions" ? <RevisionsTab volume={v} scope={scope} /> : null}
        </>
      ) : null}
      {actions.dialog}
    </DetailPage>
  );
}
